/**
 * useOptimisticTimeline - Hook for managing optimistic timeline updates
 * 
 * Provides instant UI updates for voice journals before server confirmation.
 * Automatically handles rollback on errors and retry logic.
 */

import { useState, useCallback, useRef } from 'react';
import { type TimelineEvent } from '@/services/contactTimelineService';
import { toast } from 'sonner';

export interface OptimisticEntry {
  id: string;
  event: TimelineEvent;
  status: 'pending' | 'confirmed' | 'failed';
  retryCount: number;
}

export interface OptimisticTimelineEvent extends TimelineEvent {
  optimisticStatus?: 'pending' | 'failed';
  optimisticRetryCount?: number;
}

interface UseOptimisticTimelineReturn {
  optimisticEvents: OptimisticTimelineEvent[];
  addOptimisticEvent: (event: TimelineEvent) => string;
  confirmEvent: (optimisticId: string, serverId?: string) => void;
  failEvent: (optimisticId: string, error?: string) => void;
  retryFailedEvents: () => Promise<void>;
  clearOptimistic: () => void;
  getOptimisticEntries: () => Map<string, OptimisticEntry>;
}

export function useOptimisticTimeline(
  baseEvents: TimelineEvent[] = [],
  onRetry?: (event: TimelineEvent) => Promise<void>
): UseOptimisticTimelineReturn {
  const [optimisticEntries, setOptimisticEntries] = useState<Map<string, OptimisticEntry>>(new Map());
  const retryTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Combine base events with optimistic entries, adding status metadata
  const optimisticEvents: OptimisticTimelineEvent[] = [
    ...Array.from(optimisticEntries.values())
      .filter(entry => entry.status === 'pending' || entry.status === 'failed')
      .map(entry => ({
        ...entry.event,
        optimisticStatus: entry.status,
        optimisticRetryCount: entry.retryCount,
      })),
    ...baseEvents
  ];

  /**
   * Add an optimistic event immediately to the timeline
   * Returns the optimistic ID for tracking
   */
  const addOptimisticEvent = useCallback((event: TimelineEvent): string => {
    const optimisticId = `optimistic_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    
    const optimisticEntry: OptimisticEntry = {
      id: optimisticId,
      event: {
        ...event,
        id: event.id || optimisticId,
      },
      status: 'pending',
      retryCount: 0,
    };

    setOptimisticEntries(prev => {
      const newMap = new Map(prev);
      newMap.set(optimisticId, optimisticEntry);
      return newMap;
    });

    return optimisticId;
  }, []);

  /**
   * Confirm an optimistic event after server success
   */
  const confirmEvent = useCallback((optimisticId: string, serverId?: string) => {
    setOptimisticEntries(prev => {
      const newMap = new Map(prev);
      const entry = newMap.get(optimisticId);
      
      if (entry) {
        // Update the entry with confirmed status and server ID if provided
        newMap.set(optimisticId, {
          ...entry,
          event: serverId ? { ...entry.event, id: serverId } : entry.event,
          status: 'confirmed',
        });

        // Remove confirmed entry after a short delay (it's in baseEvents now)
        setTimeout(() => {
          setOptimisticEntries(current => {
            const updated = new Map(current);
            updated.delete(optimisticId);
            return updated;
          });
        }, 1000);
      }
      
      return newMap;
    });

    // Clear any retry timeout
    const timeout = retryTimeouts.current.get(optimisticId);
    if (timeout) {
      clearTimeout(timeout);
      retryTimeouts.current.delete(optimisticId);
    }
  }, []);

  /**
   * Mark an optimistic event as failed and schedule retry
   */
  const failEvent = useCallback((optimisticId: string, error?: string) => {
    setOptimisticEntries(prev => {
      const newMap = new Map(prev);
      const entry = newMap.get(optimisticId);
      
      if (entry) {
        const updatedEntry: OptimisticEntry = {
          ...entry,
          status: 'failed',
          retryCount: entry.retryCount + 1,
        };
        
        newMap.set(optimisticId, updatedEntry);

        // Schedule retry if we haven't exceeded max retries
        if (updatedEntry.retryCount < 3 && onRetry) {
          const backoffDelay = Math.min(1000 * Math.pow(2, updatedEntry.retryCount), 10000);
          
          const timeout = setTimeout(() => {
            onRetry(entry.event)
              .then(() => {
                confirmEvent(optimisticId);
                toast.success('Journal synced successfully');
              })
              .catch(() => {
                failEvent(optimisticId, 'Retry failed');
              });
          }, backoffDelay);

          retryTimeouts.current.set(optimisticId, timeout);
        } else if (updatedEntry.retryCount >= 3) {
          // Max retries exceeded, notify user
          toast.error(`Failed to sync journal after ${updatedEntry.retryCount} attempts. Will retry later.`);
          
          // Remove from optimistic list after 5 seconds
          setTimeout(() => {
            setOptimisticEntries(current => {
              const updated = new Map(current);
              updated.delete(optimisticId);
              return updated;
            });
          }, 5000);
        }
      }
      
      return newMap;
    });
  }, [onRetry, confirmEvent]);

  /**
   * Manually retry all failed events
   */
  const retryFailedEvents = useCallback(async () => {
    const failed = Array.from(optimisticEntries.values())
      .filter(entry => entry.status === 'failed');

    if (failed.length === 0) {
      toast.info('No failed events to retry');
      return;
    }

    toast.info(`Retrying ${failed.length} failed journal${failed.length > 1 ? 's' : ''}...`);

    for (const entry of failed) {
      if (onRetry) {
        try {
          await onRetry(entry.event);
          confirmEvent(entry.id);
        } catch (error) {
          console.error('Retry failed:', error);
        }
      }
    }
  }, [optimisticEntries, onRetry, confirmEvent]);

  /**
   * Clear all optimistic entries (useful for refresh/reload)
   */
  const clearOptimistic = useCallback(() => {
    // Clear all retry timeouts
    retryTimeouts.current.forEach(timeout => clearTimeout(timeout));
    retryTimeouts.current.clear();
    
    setOptimisticEntries(new Map());
  }, []);

  /**
   * Get all optimistic entries (for debugging/inspection)
   */
  const getOptimisticEntries = useCallback(() => {
    return new Map(optimisticEntries);
  }, [optimisticEntries]);

  return {
    optimisticEvents,
    addOptimisticEvent,
    confirmEvent,
    failEvent,
    retryFailedEvents,
    clearOptimistic,
    getOptimisticEntries,
  };
}
